'use client';

import { useState } from 'react';
import { FaCheck, FaTimes, FaEye, FaDownload, FaEdit } from 'react-icons/fa';
import { motion } from 'framer-motion';

type Booking = {
  id: string;
  guestName: string;
  checkIn: string;
  checkOut: string;
  status: 'pending' | 'confirmed' | 'cancelled' | 'completed';
  total: number;
  paymentStatus: 'paid' | 'pending' | 'refunded';
  receiptUrl?: string;
};

export default function BookingsList() {
  const [bookings, setBookings] = useState<Booking[]>([
    {
      id: 'BK001',
      guestName: 'John Doe',
      checkIn: '2025-06-15',
      checkOut: '2025-06-20',
      status: 'confirmed',
      total: 1250,
      paymentStatus: 'paid',
      receiptUrl: '/receipts/receipt1.jpg'
    },
    {
      id: 'BK002',
      guestName: 'Jane Smith',
      checkIn: '2025-06-22',
      checkOut: '2025-06-25',
      status: 'pending',
      total: 750,
      paymentStatus: 'pending',
      receiptUrl: '/receipts/receipt2.jpg'
    },
    {
      id: 'BK003',
      guestName: 'Robert Johnson',
      checkIn: '2025-07-01',
      checkOut: '2025-07-07',
      status: 'confirmed',
      total: 1500,
      paymentStatus: 'paid',
      receiptUrl: '/receipts/receipt3.jpg'
    },
  ]);
  
  // State for selected booking to view details
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // Function to get status badge
  const getStatusBadge = (status: Booking['status']) => {
    switch (status) {
      case 'confirmed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Confirmed</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'cancelled':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-800">Cancelled</span>;
      case 'completed':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">Completed</span>;
      default:
        return null;
    }
  };
  
  // Function to get payment status badge
  const getPaymentBadge = (status: Booking['paymentStatus']) => {
    switch (status) {
      case 'paid':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">Paid</span>;
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-yellow-100 text-yellow-800">Pending</span>;
      case 'refunded':
        return <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">Refunded</span>;
      default:
        return null;
    }
  };
  
  // Calculate number of nights for a booking
  const calculateNights = (checkIn: string, checkOut: string) => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  
  // Function to handle booking confirmation
  const confirmBooking = (id: string) => {
    setBookings(bookings.map(booking => 
      booking.id === id ? { ...booking, status: 'confirmed' } : booking
    ));
  };
  
  // Function to handle booking cancellation
  const cancelBooking = (id: string) => {
    setBookings(bookings.map(booking => 
      booking.id === id ? { ...booking, status: 'cancelled' } : booking
    ));
  };
  
  // Function to view booking details
  const viewBookingDetails = (booking: Booking) => {
    setSelectedBooking(booking);
  };
  
  // Function to close booking details modal
  const closeBookingDetails = () => {
    setSelectedBooking(null);
  };
  
  return (
    <div>
      {/* Bookings table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Booking ID
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Guest
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dates
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Payment
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Total
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {bookings.map((booking) => (
              <motion.tr 
                key={booking.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {booking.id}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {booking.guestName}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  <div>{booking.checkIn} to {booking.checkOut}</div>
                  <div className="text-xs text-gray-400">
                    {calculateNights(booking.checkIn, booking.checkOut)} nights
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getStatusBadge(booking.status)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {getPaymentBadge(booking.paymentStatus)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  RM{booking.total.toFixed(2)}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end space-x-2">
                    <button 
                      onClick={() => viewBookingDetails(booking)} 
                      className="text-blue-600 hover:text-blue-900"
                      title="View Details"
                    >
                      <FaEye />
                    </button>
                    <button 
                      onClick={() => confirmBooking(booking.id)} 
                      className="text-green-600 hover:text-green-900"
                      title="Confirm Booking"
                      disabled={booking.status !== 'pending'}
                    >
                      <FaCheck />
                    </button>
                    <button 
                      onClick={() => cancelBooking(booking.id)} 
                      className="text-red-600 hover:text-red-900"
                      title="Cancel Booking"
                      disabled={booking.status === 'cancelled' || booking.status === 'completed'}
                    >
                      <FaTimes />
                    </button>
                    <button 
                      className="text-gray-600 hover:text-gray-900"
                      title="Edit Booking"
                    >
                      <FaEdit />
                    </button>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Booking details modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div 
            className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                Booking Details - {selectedBooking.id}
              </h3>
              <button 
                onClick={closeBookingDetails}
                className="text-gray-400 hover:text-gray-500"
              >
                <FaTimes />
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Guest Information</h4>
                <p className="text-base font-medium">{selectedBooking.guestName}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Booking Status</h4>
                <div>{getStatusBadge(selectedBooking.status)}</div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Check-in Date</h4>
                <p className="text-base">{selectedBooking.checkIn}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Check-out Date</h4>
                <p className="text-base">{selectedBooking.checkOut}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Length of Stay</h4>
                <p className="text-base">{calculateNights(selectedBooking.checkIn, selectedBooking.checkOut)} nights</p>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Payment Status</h4>
                <div>{getPaymentBadge(selectedBooking.paymentStatus)}</div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-500 mb-1">Total Amount</h4>
                <p className="text-base font-medium">RM{selectedBooking.total.toFixed(2)}</p>
              </div>
              
              {selectedBooking.receiptUrl && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Receipt</h4>
                  <button className="flex items-center text-blue-600 hover:text-blue-800">
                    <FaDownload className="mr-1" /> Download Receipt
                  </button>
                </div>
              )}
            </div>
            
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                onClick={closeBookingDetails}
                className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Close
              </button>
              
              {selectedBooking.status === 'pending' && (
                <button 
                  onClick={() => {
                    confirmBooking(selectedBooking.id);
                    closeBookingDetails();
                  }}
                  className="px-4 py-2 bg-blue-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-blue-700"
                >
                  Confirm Booking
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
